import { useState } from 'react'
import { C, font, cardStyle } from '../design'
import { apiFetch } from '../utils/api'

const TEMPLATES = [
  {
    key: 'classic',
    label: 'Классический',
    desc: 'Для живого HR. Структурированный, с Summary и достижениями.',
  },
  {
    key: 'ats',
    label: 'ATS',
    desc: 'Для скрининга нейросетями. Plain text, ключевые слова, метрики.',
  },
  {
    key: 'europass',
    label: 'Europass',
    desc: 'Формальный европейский стандарт. Фиксированная структура.',
  },
]

export default function CVGenerator({ userId = 'user' }) {
  const isMobile = window.innerWidth < 640

  // Step: 'role' | 'interview' | 'generate' | 'done'
  const [step, setStep] = useState('role')
  const [role, setRole] = useState('')
  const [template, setTemplate] = useState('classic')

  // Interview
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState({}) // {q.id: text}
  const [loadingInterview, setLoadingInterview] = useState(false)
  const [interviewError, setInterviewError] = useState(null)

  // Generate
  const [loadingGen, setLoadingGen] = useState(false)
  const [genError, setGenError] = useState(null)
  const [downloadUrl, setDownloadUrl] = useState(null)
  const [downloadName, setDownloadName] = useState('')

  const startInterview = async () => {
    if (!role.trim()) return
    setLoadingInterview(true)
    setInterviewError(null)
    try {
      const res = await apiFetch('/api/career/cv-interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, role: role.trim() }),
      })
      const data = await res.json()
      if (data.error) { setInterviewError(data.error); return }
      setQuestions(data.questions || [])
      setAnswers({})
      setStep('interview')
    } catch (e) {
      setInterviewError(e.message)
    } finally {
      setLoadingInterview(false)
    }
  }

  const saveAndGenerate = async () => {
    setLoadingGen(true)
    setGenError(null)

    // Save non-empty answers to bot_facts
    const answersToSave = questions
      .filter(q => answers[q.id]?.trim())
      .map(q => ({ question_text: q.text, answer_text: answers[q.id].trim() }))

    try {
      if (answersToSave.length > 0) {
        await apiFetch('/api/career/cv-interview/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, answers: answersToSave }),
        })
      }

      // Generate DOCX
      const res = await apiFetch('/api/career/cv-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          role: role.trim(),
          template,
          interview_answers: answersToSave,
        }),
      })
      const data = await res.json()
      if (data.error) { setGenError(data.error); return }

      // Create download link from base64
      const bytes = atob(data.docx_b64)
      const arr = new Uint8Array(bytes.length)
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
      const blob = new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
      const url = URL.createObjectURL(blob)
      setDownloadUrl(url)
      setDownloadName(data.filename)
      setStep('done')
    } catch (e) {
      setGenError(e.message)
    } finally {
      setLoadingGen(false)
    }
  }

  const reset = () => {
    setStep('role')
    setRole('')
    setQuestions([])
    setAnswers({})
    setDownloadUrl(null)
    setDownloadName('')
    setGenError(null)
    setInterviewError(null)
  }

  // ── Shared styles ──
  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.04)',
    border: `1px solid ${C.border}`,
    borderRadius: 8, padding: '10px 14px',
    color: C.text, fontFamily: font.body, fontSize: 14,
    outline: 'none',
  }
  const btnPrimary = {
    background: C.accent, color: '#05050d',
    border: 'none', borderRadius: 8,
    padding: '10px 28px', fontFamily: font.mono,
    fontSize: 11, letterSpacing: '0.08em',
    cursor: 'pointer', fontWeight: 700,
  }
  const btnSecondary = {
    background: 'transparent',
    border: `1px solid ${C.border}`,
    borderRadius: 8, padding: '9px 20px',
    color: C.textDim, fontFamily: font.mono,
    fontSize: 11, letterSpacing: '0.08em',
    cursor: 'pointer',
  }

  // ── Breadcrumb ──
  const steps = [
    { key: 'role', label: '1. Роль' },
    { key: 'interview', label: '2. Интервью' },
    { key: 'generate', label: '3. Шаблон' },
    { key: 'done', label: '4. Готово' },
  ]
  const stepIdx = steps.findIndex(s => s.key === step)

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: isMobile ? '0 0 60px' : '0 0 60px' }}>

      {/* Breadcrumb */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 32 }}>
        {steps.map((s, i) => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{
              fontFamily: font.mono, fontSize: 10, letterSpacing: '0.06em',
              color: i <= stepIdx ? C.accent : C.textDim,
              fontWeight: i === stepIdx ? 700 : 400,
            }}>
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <span style={{ color: C.border, margin: '0 8px', fontSize: 12 }}>›</span>
            )}
          </div>
        ))}
      </div>

      {/* ── Step 1: Role ── */}
      {step === 'role' && (
        <div style={{ ...cardStyle, padding: '28px 32px' }}>
          <div style={{ fontFamily: font.display, fontSize: 22, color: C.text, marginBottom: 8 }}>
            Целевая роль
          </div>
          <div style={{ fontFamily: font.body, fontSize: 13, color: C.textDim, marginBottom: 24 }}>
            Введите должность — Claude проанализирует ваши данные и задаст точечные вопросы
            для усиления резюме.
          </div>
          <input
            style={inputStyle}
            placeholder="Например: Product Manager в fintech"
            value={role}
            onChange={e => setRole(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && role.trim() && startInterview()}
          />
          {interviewError && (
            <div style={{ color: '#e05', fontFamily: font.mono, fontSize: 11, marginTop: 12 }}>
              {interviewError}
            </div>
          )}
          <div style={{ marginTop: 20 }}>
            <button
              style={{ ...btnPrimary, opacity: !role.trim() || loadingInterview ? 0.5 : 1 }}
              disabled={!role.trim() || loadingInterview}
              onClick={startInterview}
            >
              {loadingInterview ? '◎ анализирую данные...' : '→ Начать интервью'}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Interview ── */}
      {step === 'interview' && (
        <div>
          <div style={{ ...cardStyle, padding: '24px 32px', marginBottom: 16 }}>
            <div style={{ fontFamily: font.display, fontSize: 20, color: C.text, marginBottom: 6 }}>
              Роль: <span style={{ color: C.accent }}>{role}</span>
            </div>
            <div style={{ fontFamily: font.body, fontSize: 13, color: C.textDim }}>
              Claude нашёл пробелы в данных. Ответьте на вопросы — или пропустите любой.
              Ответы сохранятся в базе фактов как профессиональные знания.
            </div>
          </div>

          {questions.map((q, i) => (
            <div key={q.id} style={{ ...cardStyle, padding: '20px 28px', marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ fontFamily: font.body, fontSize: 14, color: C.text, flex: 1, paddingRight: 16 }}>
                  <span style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim, marginRight: 8 }}>
                    {i + 1}.
                  </span>
                  {q.text}
                </div>
                {q.skippable && (
                  <span style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim, whiteSpace: 'nowrap' }}>
                    необязательно
                  </span>
                )}
              </div>
              {q.hint && (
                <div style={{
                  fontFamily: font.mono, fontSize: 10, color: C.textDim,
                  marginBottom: 10, paddingLeft: 18,
                  borderLeft: `2px solid ${C.border}`,
                }}>
                  {q.hint}
                </div>
              )}
              <textarea
                style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
                placeholder={q.skippable ? 'Пропустить или ответить...' : 'Ответ...'}
                value={answers[q.id] || ''}
                onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
              />
            </div>
          ))}

          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button style={btnSecondary} onClick={() => setStep('role')}>← Изменить роль</button>
            <button style={btnPrimary} onClick={() => setStep('generate')}>
              → Выбрать шаблон
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Template ── */}
      {step === 'generate' && (
        <div>
          <div style={{ ...cardStyle, padding: '24px 32px', marginBottom: 20 }}>
            <div style={{ fontFamily: font.display, fontSize: 20, color: C.text, marginBottom: 16 }}>
              Шаблон резюме
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {TEMPLATES.map(t => (
                <div
                  key={t.key}
                  onClick={() => setTemplate(t.key)}
                  style={{
                    ...cardStyle,
                    padding: '14px 20px',
                    cursor: 'pointer',
                    border: template === t.key
                      ? `1px solid ${C.accent}`
                      : `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', marginTop: 2, flexShrink: 0,
                    border: template === t.key ? `5px solid ${C.accent}` : `2px solid ${C.border}`,
                    transition: 'all 0.15s',
                  }} />
                  <div>
                    <div style={{ fontFamily: font.mono, fontSize: 12, color: C.text, marginBottom: 3 }}>
                      {t.label}
                    </div>
                    <div style={{ fontFamily: font.body, fontSize: 12, color: C.textDim }}>
                      {t.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {genError && (
            <div style={{ color: '#e05', fontFamily: font.mono, fontSize: 11, marginBottom: 12 }}>
              {genError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button style={btnSecondary} onClick={() => setStep('interview')}>← Назад</button>
            <button
              style={{ ...btnPrimary, opacity: loadingGen ? 0.6 : 1 }}
              disabled={loadingGen}
              onClick={saveAndGenerate}
            >
              {loadingGen ? '◎ генерирую резюме...' : '↓ Сгенерировать DOCX'}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4: Done ── */}
      {step === 'done' && (
        <div style={{ ...cardStyle, padding: '32px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
          <div style={{ fontFamily: font.display, fontSize: 24, color: C.text, marginBottom: 8 }}>
            Резюме готово
          </div>
          <div style={{ fontFamily: font.body, fontSize: 13, color: C.textDim, marginBottom: 28 }}>
            Роль: <strong style={{ color: C.accent }}>{role}</strong>
            &nbsp;·&nbsp;
            Шаблон: {TEMPLATES.find(t => t.key === template)?.label}
            {questions.filter(q => answers[q.id]?.trim()).length > 0 && (
              <>
                &nbsp;·&nbsp;
                <span style={{ color: C.textDim }}>
                  {questions.filter(q => answers[q.id]?.trim()).length} ответов сохранено в факты
                </span>
              </>
            )}
          </div>
          <a
            href={downloadUrl}
            download={downloadName}
            style={{
              ...btnPrimary,
              display: 'inline-block',
              textDecoration: 'none',
              marginBottom: 16,
            }}
          >
            ↓ Скачать {downloadName}
          </a>
          <div>
            <button style={btnSecondary} onClick={reset}>
              ↺ Новое резюме
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
